/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { Link, useHistory } from 'react-router-dom';
import { Button, Text } from '../..';
import { FC, useContext, useEffect } from 'react';
import { useMutation } from 'react-query';
import { Copy, ROUTES } from '../../../types';
import { appStore } from '../../../state';
import { usePatchSoftCancellation, useGetBillingStatus, useGetShowCancelButton, useGetShowRenewButton } from '../../../hooks';
import { generateSignedRequestConfig, pricePresenter, textReplacer } from '../../../utils';
import { baseURL, postVfPayment } from '../../../api';

const selectPlanLink = (localeCopy: any, fallbackCopy: any) => {
  return (
    <Link
      css={{
        alignSelf: 'flex-end',
      }}
      to={ROUTES.PLAN_SELECTOR}
    >
      <Text>
        {localeCopy?.subscriptionInfo?.selectPlan ||
          fallbackCopy.subscriptionInfo.selectPlan}
      </Text>
    </Link>
  )
};

const areYouSureLink = (text: string, align: string) => {
  return (
    <Text
      css={{
        textAlign: align,
        marginLeft: '1em',
        marginBottom: '0.5em',
        marginTop: '0.5em'
      }}
      variant='link'>
      {text}
    </Text>
  )
};

const isDateInFuture = (date: string) => {
  return new Date(date) > new Date();
}

interface DashboardCancelSubscriptionProps extends Copy {
}

const DashboardCancelSubscription: FC<DashboardCancelSubscriptionProps> = ({ fallbackCopy, localeCopy }) => {
  const { dispatch, state } = useContext(appStore);
  const { billingStatus, user, config, paymentButtons } = state;
  const { showConfirmButtons, showCancelButton, showRenewButton } = paymentButtons;
  const { apiKey, apiSecret, countryCode, id } = user;
  const { currencySymbols } = config;
  const history = useHistory();

  const findCurrentPurchase = () => {
    return billingStatus.purchases.find(purchase => purchase.product.id !== 0 && isDateInFuture(purchase.nextBillDate));
  }

  const purchaseId: string = findCurrentPurchase()?.id || '';

  const softCancelQuery = usePatchSoftCancellation({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
    purchaseId
  });

  const billingStatusQuery = useGetBillingStatus({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const showCancelButtonQuery = useGetShowCancelButton({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
    purchaseId,
    paymentButtons
  });

  const showRenewButtonQuery = useGetShowRenewButton({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
    purchaseId,
    paymentButtons
  });

  const {
    mutateAsync: postVfPaymentData,
  } = useMutation(postVfPayment, {
    onError: async (error: any) => {
      const { message } = await error;
      console.error('Error posting VF payment: ', await error);
      if (message === 'User has active renewal subscription!') {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.HAS_ACTIVE_RENEWAL ||
              fallbackCopy.errors.HAS_ACTIVE_RENEWAL,
            timeout: 7,
            type: 'error',
          },
        });
        return;
      }

      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          dismissable: true,
          id: new Date().getTime(),
          text:
            localeCopy?.errors?.PAYMENT_ERROR ||
            fallbackCopy.errors.PAYMENT_ERROR,
          timeout: 7,
          type: 'error',
        },
      });
    },
    onSuccess: (data: any) => {
      window.open(data.paymentPageUrl, '_self');
    },
  });

  const handleClickCancelConfirmButtonYes = () => {
    softCancelQuery.refetch()
      .then(() => billingStatusQuery.refetch())
      .then((res) => {
        if (res?.data) {
          showCancelButtonQuery.refetch();
          showRenewButtonQuery.refetch();
        }
      })
      .then(() => {
        dispatch({
          type: 'UPDATE_PAYMENT_BUTTONS',
          payload: {
            ...paymentButtons,
            showConfirmButtons: false
          }
        })
      })
      .then(() => history.push(ROUTES.DASHBOARD))
  }

  const handleClickCancelConfirmButtonNo = () => {
    dispatch({
      type: 'UPDATE_PAYMENT_BUTTONS',
      payload: {
        ...paymentButtons,
        showConfirmButtons: false
      }
    })
  }

  const handleClickRenewConfirmButtonYes = async () => {
    await postVfPaymentData({
      config: generateSignedRequestConfig({
        apiKey,
        apiSecret,
        countryCode,
        data: {
          origin: document.location.origin,
          productId: findCurrentPurchase()?.product?.id,
          isRenewal: true
        },
        id,
        method: 'POST',
        url: `${baseURL}/api/billing/vodafone/user/${id}/paymentdata`,
      }),
    })
      .catch(() => { })
      .then(() => {
        dispatch({
          type: 'UPDATE_PAYMENT_BUTTONS',
          payload: {
            ...paymentButtons,
            showConfirmButtons: false
          }
        })
        showCancelButtonQuery.refetch();
        showRenewButtonQuery.refetch();
      });
  }

  const handleClickRenewConfirmButtonNo = () => {
    dispatch({
      type: 'UPDATE_PAYMENT_BUTTONS',
      payload: {
        ...paymentButtons,
        showConfirmButtons: false
      }
    })
  }

  const confirmButtons = (textYes: string, textNo: string, onClickYes: any, onClickNo: any) => {
    return (
      <div>
        <Button
          variant='small'
          onClick={onClickYes}>
          {textYes}
        </Button>
        <Button
          variant='small'
          onClick={onClickNo}>
          {textNo}
        </Button>
      </div>
    )
  }

  const cancelSubscriptionButtons = () => {
    const textYes = localeCopy?.subscriptionInfo?.confirmCancellationButtonYes || fallbackCopy.subscriptionInfo.confirmCancellationButtonYes;
    const textNo = localeCopy?.subscriptionInfo?.confirmCancellationButtonNo || fallbackCopy.subscriptionInfo.confirmCancellationButtonNo;
    return confirmButtons(textYes,
      textNo,
      handleClickCancelConfirmButtonYes,
      handleClickCancelConfirmButtonNo);
  }

  const renewSubscriptionButtons = () => {
    const textYes = localeCopy?.subscriptionInfo?.confirmCancellationButtonYes || fallbackCopy.subscriptionInfo.confirmCancellationButtonYes;
    const textNo = localeCopy?.subscriptionInfo?.confirmCancellationButtonNo || fallbackCopy.subscriptionInfo.confirmCancellationButtonNo;
    return confirmButtons(textYes,
      textNo,
      handleClickRenewConfirmButtonYes,
      handleClickRenewConfirmButtonNo);
  }

  const handleClickCancelSubscriptionLink = () => {
    dispatch({
      type: 'UPDATE_PAYMENT_BUTTONS',
      payload: {
        ...paymentButtons,
        showConfirmButtons: true
      }
    })
  }

  const handleClickRenewSubscriptionLink = () => {
    dispatch({
      type: 'UPDATE_PAYMENT_BUTTONS',
      payload: {
        ...paymentButtons,
        showConfirmButtons: true
      }
    })
  }

  const cancelSubscriptionLink = (text: string, onClick: any) => {
    return (
      <Button
        css={{
          alignSelf: 'flex-end',
          fontSize: 16,
          color: 'blue',
          float: 'right',
          marginTop: '0.25em'
        }}
        onClick={onClick}
        variant='link'>
        {text}
      </Button>
    );
  }

  const cancelSubscriptionLinkBlock = (localeCopy: any, fallbackCopy: any) => {
    let purchase = findCurrentPurchase();
    if (!purchase) { return; }
    let nextBillingDate = new Date(purchase.nextBillDate).toLocaleDateString().replace(/\//g, '-');
    let expirationDateText = localeCopy?.subscriptionInfo?.expirationText || fallbackCopy.subscriptionInfo.expirationText;
    return purchase?.endBillDate ? `${expirationDateText} ${nextBillingDate}` : cancelSubscriptionLink(localeCopy?.subscriptionInfo?.cancelLink ||
      fallbackCopy.subscriptionInfo.cancelLink, handleClickCancelSubscriptionLink);
  }

  const cancelSubscriptionButtonsBlock = (localeCopy: any, fallbackCopy: any) => {
    return (
      <div>
        {areYouSureLink(localeCopy?.subscriptionInfo?.confirmCancellationText || fallbackCopy.subscriptionInfo.confirmCancellationText, 'center')}
        {cancelSubscriptionButtons()}
      </div>);
  }

  const renewalInfo = (localeCopy: any, fallbackCopy: any) => {
    return (<Text color='secondary'>
      {localeCopy?.subscriptionInfo?.renewalInfo || fallbackCopy.subscriptionInfo.renewalInfo}
    </Text>);
  }

  const renewSubscriptionButtonsBlock = (localeCopy: any, fallbackCopy: any) => {
    return (<div css={{
      textAlign: 'end'
    }}>
      {renewalInfo(localeCopy, fallbackCopy)}
      {areYouSureLink(textReplacer(
        localeCopy?.subscriptionInfo?.confirmRenewalText ||
        fallbackCopy.subscriptionInfo.confirmRenewalText,
        {
          '{PRICE}': pricePresenter(findCurrentPurchase()?.product?.price, currencySymbols, findCurrentPurchase()?.product?.currencyCode),
        }).toString(), 'end')}
      {renewSubscriptionButtons()}
    </div>);
  }

  const renewSubscriptionLinkBlock = (localeCopy: any, fallbackCopy: any) => {
    return (
      <div>
        {renewalInfo(localeCopy, fallbackCopy)}
        {cancelSubscriptionLink(localeCopy?.subscriptionInfo?.renewLink || fallbackCopy.subscriptionInfo.renewLink, handleClickRenewSubscriptionLink)}
      </div>
    )
  }

  const cancelSubscriptionBlock = (showConfirmButtons: boolean, showCancelButton: boolean, localeCopy: any, fallbackCopy: any) => {
    return showConfirmButtons ?
      cancelSubscriptionButtonsBlock(localeCopy, fallbackCopy) :
      cancelSubscriptionLinkBlock(localeCopy, fallbackCopy);
  }

  const renewSubscriptionBlock = (showConfirmButtons: boolean, localeCopy: any, fallbackCopy: any) => {
    return showConfirmButtons ?
      renewSubscriptionButtonsBlock(localeCopy, fallbackCopy) :
      renewSubscriptionLinkBlock(localeCopy, fallbackCopy);
  }

  const showCancelAndRenewBlocks = (showConfirmButtons: boolean, showCancelButton: boolean, showRenewButton: boolean, localeCopy: any, fallbackCopy: any) => {
    return (
      <div>
        {showRenewButton ?
          (showCancelButton ?
            undefined :
            renewSubscriptionBlock(showConfirmButtons, localeCopy, fallbackCopy)) :
          cancelSubscriptionBlock(showConfirmButtons, showCancelButton, localeCopy, fallbackCopy)}
      </div>)
  }

  useEffect(() => {
    history.replace(ROUTES.DASHBOARD)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingStatus, paymentButtons, user])

  return (
    <div
      css={{
        alignSelf: 'flex-end',
      }}>
      {billingStatus.billingStatus === 'FREE_REGISTERED' ?
        selectPlanLink(localeCopy, fallbackCopy) :
        showCancelAndRenewBlocks(showConfirmButtons, showCancelButton, showRenewButton, localeCopy, fallbackCopy)
      }
    </div>
  );
};

export default DashboardCancelSubscription;